import {
    HttpEvent,
    HttpHandlerFn,
    HttpHeaderResponse,
    HttpInterceptorFn,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import {
    from,
    Observable,
    of
} from 'rxjs';
import {
    map,
    switchMap
} from 'rxjs/operators';

import { CryptoService } from '@/service';

export const EncryptionInterceptor: HttpInterceptorFn = (
    request: HttpRequest<unknown>,
    next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {

    const cryptoService = inject(CryptoService);

    return next(request).pipe(
        switchMap(event => {
            if (event instanceof HttpResponse && !(event.body instanceof Blob)) {
                const aes = event.headers.get('X-Aes-Key');
                if (aes) {
                    return from(cryptoService.setAesKey(aes)).pipe(
                        map(() => event)
                    );
                }
            }

            return of(event);
        }),
        switchMap(event => {
            if (event instanceof HttpResponse && cryptoService.hasAesKey && !(event.body instanceof Blob)) {
                return from(decrypt(event.body, cryptoService)).pipe(
                    map(body => event.clone({ body }))
                );
            }

            return of(event);
        })
    );
};

async function decrypt(object: any, crypto: CryptoService, force = false): Promise<any> {

    if (!object || typeof object !== 'object') {
        return object;
    }

    if (isArrayOfObjects(object)) {
        return Promise.all(object.map((item: any) => decrypt(item, crypto)));
    }

    let encryptedFields: string[] | '*' = object?.encrypted_fields ?? [];
    if (encryptedFields === '*' || encryptedFields.length === 0) {
        encryptedFields = Object.keys(object)
            .filter(key => key !== 'is_encrypted' && key !== 'encrypted_fields');
    }

    for (const key of Object.keys(object)) {
        const value = object[key];
        if (isArrayOfObjects(value)) {
            object[key] = await decrypt(value, crypto);
            continue;
        }
        if (typeof value === 'object' && !Array.isArray(value)) {
            object[key] = await decrypt(value, crypto, true);
            continue;
        }
        if (!encryptedFields.includes(key) || !value) {
            continue;
        }
        if (typeof value === 'string') {
            object[key] = await crypto.decrypt(value);
            continue;
        }
        if (isArrayOfStrings(value)) {
            object[key] = await Promise.all(value.map((v: any) => crypto.decrypt(v)));
            continue;
        }
    }

    return object;
}

function isArrayOfObjects(obj: any): boolean {
    return Array.isArray(obj) && obj.length > 0 && obj.every(i => typeof i === 'object');
}

function isArrayOfStrings(obj: any): boolean {
    return Array.isArray(obj) && obj.length > 0 && obj.every(i => typeof i === 'string');
}
